import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { normalizeSectionId } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** Events of a cart change, made here or elsewhere on the page. */
const CART_CHANGE_EVENTS = [
  StandardEvents.cartLinesUpdate,
  ThemeEvents.quantitySelectorUpdate,
  StandardEvents.cartDiscountUpdate,
  StandardEvents.cartNoteUpdate,
];

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    for (const type of CART_CHANGE_EVENTS) document.addEventListener(type, this.#onCartChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    for (const type of CART_CHANGE_EVENTS) document.removeEventListener(type, this.#onCartChange);
    this.#cancelRestore();
  }

  /**
   * Number of the last cart reading or cart change seen by this component: the reading made when a page comes back from
   * the back-forward cache is used only if no cart change (quantity, line removed, update from elsewhere, discount,
   * note) happened since it started.
   * @type {number}
   */
  #cartVersion = 0;

  /**
   * Requests of the restore on its way (/cart.json, then the section): a cart change, a new restore or the removal of
   * the component aborts them.
   * @type {AbortController | null}
   */
  #restoreController = null;

  /**
   * Restore put off while a field of the cart has the focus: it runs again, with a new reading, when the field loses it.
   * @type {{ field: HTMLElement, resume: () => void } | null}
   */
  #deferredRestore = null;

  /**
   * True from the restore of the page until the cart shown is found to match the cart read (or is morphed): a restore
   * stopped by a cart change runs again once the changes on their way are settled.
   * @type {boolean}
   */
  #restoreWanted = false;

  /**
   * Cart changes whose request is on its way.
   * @type {number}
   */
  #pendingChanges = 0;

  /**
   * A cart change makes any reading of the cart still on its way out of date (a quantity typed outside this component,
   * on a product page for example, is not a cart change).
   * @param {Event} event
   */
  #onCartChange = (event) => {
    const isQuantity = event.type === ThemeEvents.quantitySelectorUpdate;
    if (isQuantity && !(event.target instanceof Node && this.contains(event.target))) return;

    this.#cartVersion++;
    this.#restoreController?.abort();

    // A quantity set here is sent a moment later (debounced), with its own cart event: the restore waits for that one.
    if (isQuantity) return;

    const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') {
      this.#resumeRestore();
      return;
    }

    this.#pendingChanges++;
    const settled = () => {
      this.#pendingChanges--;
      this.#resumeRestore();
    };
    promise.then(settled, settled);
  };

  /**
   * Back on this page with the back button (from the checkout, for example), a page restored from the back-forward
   * cache shows the cart as it was when the page was left, maybe before items were added on another page: the drawer
   * would say that the cart is empty while it has items.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    this.#restoreCart();
  };

  /**
   * Runs again a restore stopped by a cart change, once no cart change is on its way and no field of the cart has the
   * focus.
   */
  #resumeRestore() {
    const deferred = this.#deferredRestore;
    if (deferred && deferred.field.isConnected && deferred.field === document.activeElement) return;

    if (this.#restoreWanted && !this.#pendingChanges && this.isConnected) this.#restoreCart();
  }

  /**
   * Reads /cart.json and, when the cart shown no longer matches it, fetches the section again and morphs it, as after a
   * cart update made outside this component, unless a cart change happened in the meantime. While a field of the cart
   * has the focus, nothing is replaced: the restore runs again when the field loses it.
   */
  #restoreCart() {
    this.#cancelRestore();
    this.#restoreWanted = true;

    // A cart change on its way renders the cart; the restore runs again once it is settled.
    if (this.#pendingChanges) return;

    const version = ++this.#cartVersion;
    const controller = new AbortController();
    const { signal } = controller;
    this.#restoreController = controller;
    const isCurrent = () => version === this.#cartVersion && !signal.aborted && this.isConnected;

    // Requests of its own, never shared: fetchCartData hands its pending request to the product form, and getSectionHTML
    // hands its pending request to any later render of the section; after a cart change, both must read the cart as it
    // is then, never this earlier reading.
    fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
        return response.json();
      })
      .then(async (cart) => {
        if (!isCurrent()) return;

        if (this.#showsCart(cart)) {
          this.#restoreWanted = false;
          return;
        }

        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', normalizeSectionId(this.sectionId));
        url.searchParams.sort();
        const response = await fetch(url.toString(), { signal });
        if (!response.ok) throw new Error(`Failed to fetch section: ${response.status} ${response.statusText}`);
        const sectionHTML = await response.text();

        // A cart change since the reading renders the cart itself.
        if (!isCurrent()) return;

        // A field being edited is never replaced: the restore waits until it loses the focus.
        const field = document.activeElement;
        if (field instanceof HTMLElement && this.contains(field) && field.matches('input, textarea, select')) {
          const resume = () => {
            this.#deferredRestore = null;
            this.#resumeRestore();
          };
          field.addEventListener('focusout', resume, { once: true });
          this.#deferredRestore = { field, resume };
          return;
        }

        this.#restoreWanted = false;
        await morphSection(this.sectionId, sectionHTML, {
          mode: this.isDrawer ? 'hydration' : 'full',
          injectStylesheet: wasEmptyCartDrawer,
        });
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Cart sync after back navigation failed:', error);
      })
      .finally(() => {
        if (this.#restoreController === controller) this.#restoreController = null;
      });
  }

  /**
   * Stops the restore on its way, or the one put off until a field loses the focus.
   */
  #cancelRestore() {
    this.#restoreController?.abort();
    this.#restoreController = null;

    if (this.#deferredRestore) {
      this.#deferredRestore.field.removeEventListener('focusout', this.#deferredRestore.resume);
      this.#deferredRestore = null;
    }
  }

  /**
   * Whether the cart shown matches the cart read: count, each line with its quantity and its price, total, discount
   * codes and note (each of the last three when it is shown).
   * @param {any} cart - The cart of /cart.json.
   * @returns {boolean}
   */
  #showsCart(cart) {
    // Amounts are compared by their digits: « $1,299.00 » shows 129900 cents. A format without cents never matches,
    // which only fetches the section again.
    const digits = (/** @type {unknown} */ value) => String(value ?? '').replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    /** @type {any[]} */
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const shown = [`count:${parseInt(this.querySelector('[ref="cartItemCount"]')?.textContent ?? '0', 10) || 0}`];
    const read = [`count:${cart?.item_count ?? 0}`];

    for (const row of this.refs.cartItemRows ?? []) {
      const input = row.querySelector('input[name="updates[]"]');
      const price = row.querySelector('.cart-items__price text-component')?.getAttribute('value');
      shown.push(`${row.dataset.key}:${input instanceof HTMLInputElement ? input.defaultValue : ''}:${digits(price)}`);
    }
    for (const item of items) read.push(`${item.key}:${item.quantity}:${digits(item.final_line_price)}`);

    const total = this.querySelector('[ref="cartTotal"]');
    if (total) {
      shown.push(`total:${digits(total.getAttribute('value'))}`);
      read.push(`total:${digits(cart?.total_price)}`);
    }

    if (this.querySelector('cart-discount-component')) {
      const applications = [
        ...(cart?.cart_level_discount_applications ?? []),
        ...items.flatMap((item) => (item.line_level_discount_allocations ?? []).map((allocation) => allocation?.discount_application)),
      ];
      const codes = new Set(applications.filter((application) => application?.type === 'discount_code').map((application) => application.title));
      const pills = Array.from(this.querySelectorAll('.cart-discount__pill'), (pill) => pill.getAttribute('data-discount-code'));
      shown.push(`codes:${JSON.stringify(pills.sort())}`);
      read.push(`codes:${JSON.stringify([...codes].sort())}`);
    }

    const note = this.querySelector('textarea[name="note"]');
    if (note instanceof HTMLTextAreaElement) {
      const text = (/** @type {unknown} */ value) => String(value ?? '').replace(/\r\n?/g, '\n');
      shown.push(`note:${JSON.stringify(text(note.value))}`);
      read.push(`note:${JSON.stringify(text(cart?.note))}`);
    }

    return JSON.stringify(shown) === JSON.stringify(read);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
